import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Bell, LogOut, Flame } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Navbar() {
    const { t } = useTranslation();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const navigate = useNavigate();

    const navItems = [
        { to: "/intentions", label: t("nav.intentions") },
        { to: "/special", label: t("nav.special") },
        { to: "/group-prayers", label: t("nav.group") },
        { to: "/psalms", label: t("nav.psalms") },
        { to: "/liturgical", label: t("nav.liturgical") },
        { to: "/testimonies", label: t("nav.testimonies") },
    ];

    useEffect(() => {
        if (user && user.id) {
            api.get("/notifications")
                .then(({ data }) => setNotifs(data))
                .catch(() => {});
        }
    }, [user]);

    const unread = notifs.filter((n) => !n.read).length;

    const markAllRead = async () => {
        await api.post("/notifications/read-all").catch(() => {});
        setNotifs((ns) => ns.map((n) => ({ ...n, read: true })));
    };

    return (
        <header
            className="sticky top-0 z-40 border-b"
            style={{ background: "rgba(253,251,247,0.82)", backdropFilter: "blur(14px)", borderColor: "#E5E0D8" }}
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 flex items-center h-16">
                <Link to="/" className="flex items-center gap-2 mr-4 flex-shrink-0" data-testid="nav-home-link">
                    <div className="h-9 w-9 rounded-full flex items-center justify-center"
                         style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                        <Flame size={18} strokeWidth={1.6} />
                    </div>
                    <div className="leading-tight">
                        <div className="font-display text-xl" style={{ color: "#1E1B4B" }}>The Prayer Warriors</div>
                        <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#7F1D1D" }}>{t("common.salesian")}</div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-6 ml-6">
                    {navItems.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            className={({ isActive }) =>
                                `text-sm link-underline whitespace-nowrap ${isActive ? "font-semibold" : ""}`
                            }
                            style={{ color: "#1E1B4B" }}
                            data-testid={`nav-${n.to.slice(1)}-link`}
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="ml-auto flex items-center gap-1 sm:gap-2">
                    <LanguageSwitcher />
                    {user && user.id ? (
                        <>
                            <div className="relative">
                                <button
                                    className="p-2 rounded-full hover:bg-[#F5F0E6] relative"
                                    onClick={() => setNotifOpen(!notifOpen)}
                                    data-testid="nav-notifications-button"
                                >
                                    <Bell size={18} style={{ color: "#1E1B4B" }} />
                                    {unread > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full text-[10px] flex items-center justify-center"
                                              style={{ background: "#7F1D1D", color: "#FDFBF7" }}>
                                            {unread}
                                        </span>
                                    )}
                                </button>
                                {notifOpen && (
                                    <div className="absolute right-0 mt-2 w-80 sm:w-96 max-h-96 overflow-y-auto p-card p-3 z-50"
                                         data-testid="nav-notifications-dropdown">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="font-display text-lg" style={{ color: "#1E1B4B" }}>{t("common.notifications")}</div>
                                            <button onClick={markAllRead} className="text-xs underline" style={{ color: "#7F1D1D" }} data-testid="nav-notifications-readall">
                                                {t("common.readAll")}
                                            </button>
                                        </div>
                                        {notifs.length === 0 && <div className="text-sm text-neutral-500 py-6 text-center">{t("common.noNotifs")}</div>}
                                        {notifs.map((n) => (
                                            <div key={n.id} className={`py-2 border-b last:border-b-0 ${n.read ? "opacity-60" : ""}`} style={{ borderColor: "#E5E0D8" }}>
                                                <div className="text-sm font-semibold" style={{ color: "#1E1B4B" }}>{n.title}</div>
                                                <div className="text-xs text-neutral-600">{n.body}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Link to="/dashboard" className="hidden md:inline-block btn-ghost text-sm" data-testid="nav-dashboard-link">
                                {t("nav.dashboard")}
                            </Link>
                            <Link to="/profile" className="hidden md:inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full hover:bg-[#F5F0E6]" style={{ color: "#1E1B4B" }} data-testid="nav-profile-link" title={t("nav.profile")}>
                                <span className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                                    {(user.name || "U").trim().charAt(0).toUpperCase()}
                                </span>
                                <span className="hidden lg:inline font-medium">{user.name?.split(" ")[0]}</span>
                            </Link>
                            {user.role === "admin" && (
                                <Link to="/admin" className="hidden md:inline-block btn-gold text-sm" data-testid="nav-admin-link">
                                    {t("nav.moderator")}
                                </Link>
                            )}
                            <button className="p-2 rounded-full hover:bg-[#F5F0E6]" onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout-button">
                                <LogOut size={18} style={{ color: "#1E1B4B" }} />
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="btn-ghost hidden sm:inline-block text-sm" data-testid="nav-login-link">
                                {t("nav.signin")}
                            </Link>
                            <Link to="/register" className="btn-primary text-sm" data-testid="nav-register-link">
                                {t("nav.join")}
                            </Link>
                        </>
                    )}
                    <button className="xl:hidden p-2 rounded-full hover:bg-[#F5F0E6]" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
                        {open ? <X size={20} style={{ color: "#1E1B4B" }} /> : <Menu size={20} style={{ color: "#1E1B4B" }} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="xl:hidden border-t" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}>
                    <div className="px-6 py-4 flex flex-col gap-4">
                        {navItems.map((n) => (
                            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-base" style={{ color: "#1E1B4B" }} data-testid={`nav-mobile-${n.to.slice(1)}-link`}>
                                {n.label}
                            </Link>
                        ))}
                        {user && user.id && (
                            <>
                                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-base" style={{ color: "#1E1B4B" }} data-testid="nav-mobile-dashboard-link">{t("nav.dashboard")}</Link>
                                <Link to="/profile" onClick={() => setOpen(false)} className="text-base" style={{ color: "#1E1B4B" }} data-testid="nav-mobile-profile-link">{t("nav.profile")}</Link>
                                {user.role === "admin" && (
                                    <Link to="/admin" onClick={() => setOpen(false)} className="text-base" style={{ color: "#7F1D1D" }} data-testid="nav-mobile-admin-link">{t("nav.moderator")}</Link>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
